import {
  COLORS,
  drawArc,
  drawArrow,
  drawBall,
  drawDashedLine,
  drawPill,
  drawText,
  drawTrail,
  hexToRgba,
} from './shared.js';

function geometry(width, height, params) {
  const pivot = { x: width * 0.5, y: Math.max(76, height * 0.17) };
  const available = height - pivot.y - 86;
  const scale = Math.min(300, Math.max(72, available / Math.max(params.length, 0.2)));
  const length = params.length * scale;
  return { pivot, scale, length };
}

function bobPosition(frame) {
  const { width, height, params, state } = frame;
  const { pivot, length } = geometry(width, height, params);
  return {
    x: pivot.x + Math.sin(state.theta) * length,
    y: pivot.y + Math.cos(state.theta) * length,
    pivot,
    length,
  };
}

export const pendulumRenderer = {
  trailPoint(frame) {
    const { x, y } = bobPosition(frame);
    return { x, y };
  },

  draw(ctx, frame) {
    const { width, height, state, params, metrics, options, accent } = frame;
    const { pivot, length } = geometry(width, height, params);
    const bob = bobPosition(frame);
    const radius = 9 + params.mass * 2.2;
    const theta = state.theta;
    const maxTheta = (params.initialAngle * Math.PI) / 180;

    // 天花板与支架
    ctx.save();
    ctx.strokeStyle = 'rgba(148, 190, 220, 0.32)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(width * 0.26, pivot.y - 20);
    ctx.lineTo(width * 0.74, pivot.y - 20);
    ctx.stroke();
    ctx.strokeStyle = 'rgba(148, 190, 220, 0.16)';
    ctx.lineWidth = 1;
    for (let x = width * 0.27; x < width * 0.74; x += 15) {
      ctx.beginPath();
      ctx.moveTo(x, pivot.y - 20);
      ctx.lineTo(x + 8, pivot.y - 28);
      ctx.stroke();
    }
    ctx.fillStyle = 'rgba(148, 190, 220, 0.4)';
    ctx.beginPath();
    ctx.arc(pivot.x, pivot.y, 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // 量角器刻度
    const protractorRadius = Math.min(length * 0.52, 118);
    if (protractorRadius > 42) {
      drawArc(ctx, pivot, protractorRadius, Math.PI / 2 - (70 * Math.PI) / 180, Math.PI / 2 + (70 * Math.PI) / 180, {
        color: 'rgba(141, 184, 214, 0.16)',
        width: 1,
      });
      for (let deg = -70; deg <= 70; deg += 10) {
        const angle = Math.PI / 2 - (deg * Math.PI) / 180;
        const inner = protractorRadius - (deg % 30 === 0 ? 8 : 4);
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        ctx.save();
        ctx.strokeStyle = deg === 0 ? 'rgba(148,190,220,.42)' : 'rgba(148,190,220,.2)';
        ctx.beginPath();
        ctx.moveTo(pivot.x + cos * inner, pivot.y + sin * inner);
        ctx.lineTo(pivot.x + cos * protractorRadius, pivot.y + sin * protractorRadius);
        ctx.stroke();
        if (deg % 30 === 0 && deg !== 0) {
          drawText(ctx, `${Math.abs(deg)}°`, pivot.x + cos * (protractorRadius + 14), pivot.y + sin * (protractorRadius + 14), {
            color: 'rgba(170, 203, 226, 0.4)',
            font: '500 9px "Cascadia Code", monospace',
            align: 'center',
          });
        }
        ctx.restore();
      }
    }

    // 平衡位置与摆角
    drawDashedLine(ctx, pivot, { x: pivot.x, y: pivot.y + length * 1.12 }, {
      color: 'rgba(148, 190, 220, 0.2)',
      dash: [3, 6],
    });
    drawArc(ctx, pivot, 52, Math.PI / 2, Math.PI / 2 - theta, {
      color: hexToRgba(accent, 0.65),
      width: 1.6,
      anticlockwise: theta > 0,
    });
    if (Math.abs(theta) > 0.04) {
      const mid = Math.PI / 2 - theta / 2;
      drawText(ctx, `θ = ${(theta * 180 / Math.PI).toFixed(1)}°`, pivot.x + Math.cos(mid) * 74, pivot.y + Math.sin(mid) * 74, {
        color: hexToRgba(accent, 0.9),
        font: '600 11px "Cascadia Code", monospace',
        align: 'center',
      });
    }

    // 摆动轨迹弧
    drawArc(
      ctx,
      pivot,
      length,
      Math.PI / 2 - maxTheta,
      Math.PI / 2 + maxTheta,
      { color: hexToRgba(accent, 0.12), width: 1.2, dash: [2, 6] },
    );

    if (frame.trail?.length > 1) drawTrail(ctx, frame.trail, accent, { width: 2.4, alpha: 0.52 });

    // 摆线
    ctx.save();
    ctx.strokeStyle = 'rgba(226, 240, 250, 0.68)';
    ctx.lineWidth = 1.15;
    ctx.shadowColor = hexToRgba(accent, 0.35);
    ctx.shadowBlur = 7;
    ctx.beginPath();
    ctx.moveTo(pivot.x, pivot.y);
    ctx.lineTo(bob.x, bob.y);
    ctx.stroke();
    ctx.restore();

    // 摆球投影与本体
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,.22)';
    ctx.beginPath();
    ctx.ellipse(bob.x + 4, bob.y + radius + 20, radius * 0.85, radius * 0.32, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    drawBall(ctx, bob.x, bob.y, radius, accent);

    // 速度矢量
    if (options.vectors) {
      const speed = Math.abs(state.omega) * params.length;
      if (speed > 0.015) {
        const dir = Math.sign(state.omega);
        const tangent = { x: Math.cos(theta) * dir, y: -Math.sin(theta) * dir };
        const len = Math.min(94, Math.max(20, speed * 18));
        const start = { x: bob.x + tangent.x * (radius + 3), y: bob.y + tangent.y * (radius + 3) };
        const end = { x: start.x + tangent.x * len, y: start.y + tangent.y * len };
        drawArrow(ctx, start, end, {
          color: COLORS.cyan,
          width: 2.1,
          head: 8,
          label: `v ${speed.toFixed(2)}`,
          labelOffset: { x: tangent.x * 18, y: tangent.y * 18 - 8 },
          glow: true,
        });
      }

      // 重力
      const gravityLength = Math.min(62, Math.max(20, params.mass * params.gravity * 1.15));
      const gravityStart = { x: bob.x, y: bob.y + radius + 2 };
      drawArrow(
        ctx,
        gravityStart,
        { x: gravityStart.x, y: gravityStart.y + gravityLength },
        {
          color: COLORS.pink,
          width: 1.7,
          head: 7,
          alpha: 0.9,
          label: 'mg',
          labelOffset: { x: 19, y: 4 },
        },
      );

      // 绳张力（指向悬点）
      const tension = metrics.extras.tension;
      if (tension > 0) {
        const dx = pivot.x - bob.x;
        const dy = pivot.y - bob.y;
        const norm = Math.hypot(dx, dy) || 1;
        const tensionLength = Math.min(80, Math.max(16, tension * 2.4));
        const start = { x: bob.x + (dx / norm) * (radius + 3), y: bob.y + (dy / norm) * (radius + 3) };
        drawArrow(
          ctx,
          start,
          { x: start.x + (dx / norm) * tensionLength, y: start.y + (dy / norm) * tensionLength },
          {
            color: COLORS.mint,
            width: 1.6,
            head: 7,
            alpha: 0.8,
            label: 'FT',
            labelOffset: { x: -16, y: -4 },
          },
        );
      }
    }

    // 底部读数
    const bottomY = height - 30;
    drawPill(ctx, `T₀ ${metrics.extras.smallPeriod.toFixed(2)} s`, width * 0.5 - 116, bottomY, {
      color: COLORS.text,
      bg: 'rgba(7, 18, 30, 0.7)',
      border: 'rgba(148, 190, 220, 0.14)',
      font: '600 11px "Cascadia Code", monospace',
    });
    drawPill(ctx, `E ${metrics.energy.total.toFixed(2)} J`, width * 0.5 - 18, bottomY, {
      color: accent,
      bg: hexToRgba(accent, 0.07),
      border: hexToRgba(accent, 0.24),
      font: '600 11px "Cascadia Code", monospace',
    });
    drawPill(ctx, `L ${params.length.toFixed(2)} m`, width * 0.5 + 86, bottomY, {
      color: COLORS.muted,
      bg: 'rgba(7, 18, 30, 0.6)',
      border: 'rgba(148, 190, 220, 0.12)',
      font: '500 11px "Cascadia Code", monospace',
    });
  },
};
